import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { saveScheduledReport, sendReportEmail } from '../../../services/reportService';

const ScheduleReportModal = ({ reportType, filters, reportData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    reportName: '',
    recipients: '',
    frequency: 'once',
    scheduleTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const frequencyOptions = [
    { value: 'once', label: 'Send Once' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData?.reportName?.trim()) {
      setError('Report name is required');
      return false;
    }
    if (!formData?.recipients?.trim()) {
      setError('At least one recipient email is required');
      return false;
    }
    
    // Validate email format
    const emails = formData?.recipients?.split(',')?.map(e => e?.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails?.filter(email => !emailRegex?.test(email));
    if (invalidEmails?.length > 0) {
      setError(`Invalid email format: ${invalidEmails?.join(', ')}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const recipients = formData?.recipients?.split(',')?.map(e => e?.trim());
      
      // Save scheduled report configuration
      const reportConfig = {
        reportName: formData?.reportName,
        reportType,
        filters: JSON.stringify(filters),
        recipients: JSON.stringify(recipients),
        frequency: formData?.frequency,
        scheduleTime: formData?.scheduleTime || null,
        isActive: true
      };
      
      const saveResult = await saveScheduledReport(reportConfig);
      
      if (saveResult?.error) {
        throw new Error('Failed to save scheduled report');
      }
      
      // If frequency is 'once', send email immediately
      if (formData?.frequency === 'once') {
        await sendReportEmail(reportData, recipients, reportType);
      }
      
      onSuccess();
    } catch (error) {
      console.error('Schedule report error:', error);
      setError(error?.message || 'Failed to schedule report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Mail" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Schedule Email Delivery</h2>
              <p className="text-sm text-muted-foreground">Send reports to stakeholders automatically</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Report Name"
            name="reportName"
            placeholder="e.g., Monthly Player Performance Report"
            value={formData?.reportName}
            onChange={handleChange}
            required
          />

          <Input
            label="Recipients"
            name="recipients"
            placeholder="email1@example.com, email2@example.com"
            value={formData?.recipients}
            onChange={handleChange}
            description="Separate multiple emails with commas"
            required
          />

          <Select
            label="Frequency"
            options={frequencyOptions}
            value={formData?.frequency}
            onChange={(value) => handleSelectChange('frequency', value)}
            description="How often should this report be sent?"
          />

          {formData?.frequency !== 'once' && (
            <Input
              label="Schedule Time"
              name="scheduleTime"
              type="time"
              value={formData?.scheduleTime}
              onChange={handleChange}
              description="Time of day to send the report"
            />
          )}

          {/* Report Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-medium text-foreground">Report Summary</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Type: <span className="capitalize font-medium">{reportType}</span></p>
              <p>• Records: <span className="font-medium">{reportData?.length}</span></p>
              <p>• Filters: Position ({filters?.position}), League ({filters?.league}), Timeframe ({filters?.timeframe})</p>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isSubmitting}
              iconName="Send"
              iconPosition="left"
            >
              {formData?.frequency === 'once' ? 'Send Now' : 'Schedule Report'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleReportModal;